import { useRouter } from "@tanstack/react-router";
import { cn } from "tailwind-variants";
import { useGetUmbracoDeliveryApiV2LocalizationSuspense } from "#/_generated/umbraco/delivery-api/features/languages/languages.ts";
import type {
	GoogleTranslateLanguageSelectorUmbBlockElementModel,
	GoogleTranslateLanguageSelectorUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { tryGetButtonVariant } from "#/_packages/umbrella/features/button/utils";
import {
	type LanguageIconPosition,
	LanguageSelector,
	type LanguageTextLabelVariant,
	PreviewLanguageSelector,
} from "#/_packages/umbrella/features/google-translate-language-selector/components/google-translate-language-selector";
import { tryGetIconSize } from "#/_packages/umbrella/features/icons/components/umb-sprite-icon.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { tryGetIconPosition } from "#/_packages/umbrella/integrations/umbraco/utils/global.ts";
import {
	tryGetAlignment,
	tryGetIntent,
	tryGetSize,
} from "#/_packages/umbrella/utils";

type LanguageSelectorBlockProps = BlockItemProps & {
	content: GoogleTranslateLanguageSelectorUmbBlockElementModel;
	settings: GoogleTranslateLanguageSelectorUmbBlockSettingsElementModel;
};

const languageTextLabelVariants = ["none", "abbreviation", "text"] as const;

function tryGetLanguageTextLabelVariant(
	value?: string | null,
): LanguageTextLabelVariant {
	return languageTextLabelVariants.includes(value as LanguageTextLabelVariant)
		? (value as LanguageTextLabelVariant)
		: "text";
}

export default function GoogleTranslateLanguageSelectorUmbBlock({
	className,
	content,
	settings,
}: LanguageSelectorBlockProps) {
	const isPreview = !!useRouter().options.context.previewBlock;

	const alignmentSelfVertical = tryGetAlignment(
		settings.properties?.alignmentSelfVertical,
		"stretch",
	);
	const alignmentClass =
		alignmentSelfVertical === "stretch" ? "" : `self-${alignmentSelfVertical}`;

	return (
		<div
			id={settings.properties?.generatedId ?? content.id}
			className={cn(alignmentClass, className)}
		>
			{isPreview ? (
				<Preview content={content} settings={settings} />
			) : (
				<Default content={content} settings={settings} />
			)}
		</div>
	);
}

function Default({ content }: LanguageSelectorBlockProps) {
	const { data } = useGetUmbracoDeliveryApiV2LocalizationSuspense();
	const languages =
		data.data?.map((item) => ({
			...item,
			culture: item.culture.split("-")[0],
		})) ?? [];

	return (
		<LanguageSelector
			languages={languages}
			textLabelVariant={tryGetLanguageTextLabelVariant(
				content.properties?.textLabelVariant,
			)}
			size={tryGetSize(content.properties?.size)}
			intentColor={tryGetIntent(content.properties?.intentColor)}
			typeVariant={tryGetButtonVariant(content.properties?.typeVariant)}
			icon={content.properties?.icon}
			iconPosition={
				tryGetIconPosition(
					content.properties?.iconPosition,
				) as LanguageIconPosition
			}
			iconSize={tryGetIconSize(content.properties?.size)}
		/>
	);
}

function Preview({ content }: LanguageSelectorBlockProps) {
	return (
		<PreviewLanguageSelector
			languages={[]}
			textLabelVariant={tryGetLanguageTextLabelVariant(
				content.properties?.textLabelVariant,
			)}
			size={tryGetSize(content.properties?.size)}
			intentColor={tryGetIntent(content.properties?.intentColor)}
			typeVariant={tryGetButtonVariant(content.properties?.typeVariant)}
			icon={content.properties?.icon}
			iconPosition={
				tryGetIconPosition(
					content.properties?.iconPosition,
				) as LanguageIconPosition
			}
			iconSize={tryGetIconSize(content.properties?.size)}
		/>
	);
}
